/**
 * Author: Claude Code
 * Generation: Main React app with complete routing system and i18n support
 * Version: v3.2.229
 * Modified: 2025-11-05 (Add GoogleOAuthProvider for FedCM compatibility)
 */
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Navbar from './components/Navbar';
import Background from './components/Background';
import AuthModal from './components/AuthModal';
import WaitingRoom from './components/WaitingRoom';
import GlobalLoadingOverlay from './components/GlobalLoadingOverlay';
import FooterICP from './components/FooterICP';
import LandingPage from './pages/LandingPage';
import RulesPage from './pages/RulesPage';
import LobbyPage from './pages/LobbyPage';
import AgentCreationPage from './pages/AgentCreationPage';
import GameApp from './components/GameApp';

// Test & Debug Pages (Development Only)
import { GamePage } from './pages/GamePage';
import CompleteTest_FrontBackend from './pages/CompleteTest_FrontBackend';
import AnimationTestPage from './pages/AnimationTestPage';
import AgentSpawnedRealTest from './pages/AgentSpawnedRealTest';
import SingleAgentMicroMapTest from './pages/SingleAgentMicroMapTest';
import MapExporter from './pages/MapExporter';
import LoadingTestPage from './pages/LoadingTestPage'; // P0.4: Loading scene test
import LoadingScreenTest from './pages/LoadingScreenTest'; // v3.2.138: Mock loading test
import LocalSpriteMicroMapTest from './pages/LocalSpriteMicroMapTest';

// v3.2.62: Initialize global Phaser preload for smooth agent animations
import { PhaserPreloadService } from './services/PhaserPreloadService';

import './i18n/config'; // v3.2.54: Initialize i18next
import './styles/global.css';

// Initialize preload service at app startup (runs once)
PhaserPreloadService.initialize().catch(err => {
  console.warn('Failed to initialize PhaserPreloadService:', err);
});

function App() {
  return (
    <BrowserRouter>
      <Background />
      <Navbar />

      <Routes>
        {/* Pre-game UI routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/rules" element={<RulesPage />} />
        <Route path="/lobby" element={<LobbyPage />} />
        <Route path="/create-agent" element={<AgentCreationPage />} />

        {/* Game engine (legacy) & Spectate route (recommended) */}
        <Route path="/game" element={<GameApp />} />
        <Route path="/game/:roomId" element={<GamePage />} />

        {/* ===== Test & Debug Routes (Development Only) ===== */}
        <Route path="/game-page" element={<GamePage />} /> {/* legacy for older deep links */}
        <Route path="/test-integration" element={<CompleteTest_FrontBackend />} />
        <Route path="/test-animation" element={<AnimationTestPage />} />
        <Route path="/test-agent-spawn" element={<AgentSpawnedRealTest />} />
        <Route path="/test-micro-map" element={<SingleAgentMicroMapTest />} />
        <Route path="/map-exporter" element={<MapExporter />} />
        <Route path="/test-loading" element={<LoadingTestPage />} /> {/* P0.4: Loading scene test */}
        <Route path="/test-loading-mock" element={<LoadingScreenTest />} /> {/* v3.2.138: Mock loading event test */}
        <Route path="/test-local-sprite" element={<LocalSpriteMicroMapTest />} />

        {/* Default redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Global Modal Components */}
      <AuthModal />
      <WaitingRoom />
      <GlobalLoadingOverlay />
      <FooterICP />
    </BrowserRouter>
  );
}

const googleClientId = (import.meta as any)?.env?.VITE_GOOGLE_CLIENT_ID;

if (!googleClientId) {
  console.warn('[Main] ⚠️ Google Client ID not configured in environment');
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <GoogleOAuthProvider clientId={googleClientId || ''}>
    <App />
  </GoogleOAuthProvider>
);
